import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  Shield,
  AlertCircle,
} from 'lucide-react';
import {
  Card,
  Button,
  Heading,
  BodyText,
  Skeleton,
  Table,
  Textarea,
} from '@aaimplatform/ui';
import type { TableColumn } from '@aaimplatform/ui';
import type { components as CollateralPledgeComponents } from '@aaimplatform/institutional-types/collateral-pledge';
import { usePledgeDetail, useAcknowledgePledge } from '../hooks/usePledges';
import type {
  PledgeSelectedAccountDetail,
  PledgeSelectedHoldingDetail,
  PledgeConsentProof,
} from '../hooks/usePledges';
import {
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
  formatQuantity,
  formatRatioAsPercent,
} from '../utils/display-format';
import { ASSET_CLASS_LABELS } from '../generated/terminology-labels';
import {
  ACCOUNT_TYPE_LABEL,
  CONSENT_METHOD_LABEL,
  ChaTierBadge,
  EligibilityBadge,
  EnumText,
  PledgeStatusBadge,
} from '../utils/status-labels';
import { RouterBackLink } from '../components/RouterBackLink';

type AttestationMethod = CollateralPledgeComponents['schemas']['AttestationMethod'];

// ---------------------------------------------------------------------------
// Account table columns
// ---------------------------------------------------------------------------

/**
 * Eligibility as a badge, with the API's stated reason beside a "No". The
 * reason is prose the pledge snapshot carries (ineligibilityReason); the page
 * printed the badge and dropped it.
 */
function EligibilityCell({
  eligible,
  reason,
}: {
  eligible: boolean;
  reason?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <EligibilityBadge eligible={eligible} />
      {!eligible && reason && (
        <span className="text-xs font-sans text-tertiary leading-snug text-center">{reason}</span>
      )}
    </div>
  );
}

/**
 * "Entire account" when the pledge names no holdings in the account, else the
 * count of holdings pledged. The spec reads an absent or empty holdings list
 * as the whole account pledged.
 */
function pledgedScope(row: PledgeSelectedAccountDetail): string {
  const count = row.holdings?.length ?? 0;
  if (count === 0) return 'Entire account';
  return `${formatCount(count)} holding${count === 1 ? '' : 's'}`;
}

const ACCOUNT_COLUMNS: TableColumn<PledgeSelectedAccountDetail>[] = [
  {
    key: 'accountName',
    label: 'Account',
    render: (_value, row) => (
      <div>
        <div className="text-sm font-sans font-medium text-primary leading-normal">
          {row.accountName}
        </div>
        {row.institutionName && (
          <div className="text-xs font-sans text-tertiary leading-normal">
            {row.institutionName}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'accountType',
    label: 'Type',
    render: (_value, row) => (
      <EnumText
        map={ACCOUNT_TYPE_LABEL}
        value={row.accountType}
        className="text-sm font-sans text-secondary leading-normal"
      />
    ),
  },
  {
    key: 'assetClass',
    label: 'Asset Class',
    render: (_value, row) => (
      <EnumText
        map={ASSET_CLASS_LABELS}
        value={row.assetClass}
        className="text-sm font-sans text-secondary leading-normal"
      />
    ),
  },
  {
    // currentBalance is the ledger (posted) balance and availableBalance the
    // spendable one (platform-admin.openapi.yaml PledgeSelectedAccountDetail).
    // The page printed the ledger balance under "Available" whenever no
    // available balance was sent; each figure now prints under its own name,
    // and an absent available balance prints "--".
    key: 'currentBalance',
    label: 'Ledger Balance',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatMoney(row.currentBalance)}
      </span>
    ),
  },
  {
    key: 'availableBalance',
    label: 'Available',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatMoney(row.availableBalance)}
      </span>
    ),
  },
  {
    key: 'ltvRatio',
    label: 'LTV',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
        {formatRatioAsPercent(row.ltvRatio)}
      </span>
    ),
  },
  {
    key: 'haircutPercent',
    label: 'Haircut',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
        {formatRatioAsPercent(row.haircutPercent)}
      </span>
    ),
  },
  {
    key: 'pledgeableAmount',
    label: 'Pledgeable',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatMoney(row.pledgeableAmount)}
      </span>
    ),
  },
  {
    key: 'eligible',
    label: 'Eligible',
    align: 'center',
    render: (_value, row) => (
      <EligibilityCell eligible={row.eligible} reason={row.ineligibilityReason} />
    ),
  },
  {
    key: 'holdings',
    label: 'Pledged',
    render: (_value, row) => (
      <span className="text-sm font-sans text-secondary leading-normal">{pledgedScope(row)}</span>
    ),
  },
];

// ---------------------------------------------------------------------------
// Holding table columns
// ---------------------------------------------------------------------------

/**
 * Label for each capture method. Keyed on the generated AttestationMethod
 * enum, so a method added to collateral-pledge.openapi.yaml fails tsc here
 * until it has a label. A stored value outside the enum prints "Unrecognized"
 * through EnumText, never the raw code.
 */
const ATTESTATION_METHOD_LABEL: Record<AttestationMethod, string> = {
  ADVISOR_KEYSTROKE: 'Advisor keystroke attestation',
  MEMBER_SELF_ATTESTED: 'Member self-attestation',
};

/**
 * The holdings pledged within one account: WHAT is pledged, as security and
 * units, with the per-line value, LTV, eligibility and attestation provenance
 * the pledge snapshot carries (PledgeSelectedHoldingDetail).
 */
const HOLDING_COLUMNS: TableColumn<PledgeSelectedHoldingDetail>[] = [
  {
    key: 'securityName',
    label: 'Security',
    render: (_value, row) => (
      <div>
        <div className="text-sm font-sans font-medium text-primary leading-normal">
          {row.securityName}
        </div>
        {row.tickerSymbol && (
          <div className="text-xs font-mono text-tertiary leading-normal">{row.tickerSymbol}</div>
        )}
      </div>
    ),
  },
  {
    key: 'assetClass',
    label: 'Asset Class',
    render: (_value, row) => (
      <EnumText
        map={ASSET_CLASS_LABELS}
        value={row.assetClass}
        className="text-sm font-sans text-secondary leading-normal"
      />
    ),
  },
  {
    key: 'quantity',
    label: 'Quantity',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatQuantity(row.quantity)}
      </span>
    ),
  },
  {
    key: 'marketValue',
    label: 'Market Value',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatMoney(row.marketValue)}
      </span>
    ),
  },
  {
    key: 'ltvRatio',
    label: 'LTV',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
        {formatRatioAsPercent(row.ltvRatio)}
      </span>
    ),
  },
  {
    key: 'pledgeableAmount',
    label: 'Pledgeable',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatMoney(row.pledgeableAmount)}
      </span>
    ),
  },
  {
    key: 'eligible',
    label: 'Eligible',
    align: 'center',
    render: (_value, row) => (
      <EligibilityCell eligible={row.eligible} reason={row.ineligibilityReason} />
    ),
  },
  {
    // fiduciaryAttestation is true for a position a fiduciary advisor
    // attested under the direct-attested connector. Null or false covers both
    // aggregator-sourced and member self-attested holdings, and the capture
    // method (the AttestationMethod enum, collateral-pledge.openapi.yaml)
    // tells those two apart, so it prints as a label from
    // ATTESTATION_METHOD_LABEL rather than as a code.
    key: 'fiduciaryAttestation',
    label: 'Attestation',
    render: (_value, row) => {
      if (row.fiduciaryAttestation) {
        return (
          <div>
            <div className="text-sm font-sans text-primary leading-normal">Advisor attested</div>
            {row.attestationMethod ? (
              <div className="text-xs font-sans text-secondary leading-normal">
                <EnumText map={ATTESTATION_METHOD_LABEL} value={row.attestationMethod} />
              </div>
            ) : null}
            <div className="text-xs font-sans text-tertiary leading-normal">
              {row.attestationAsOfDate ? `As of ${formatDate(row.attestationAsOfDate)}` : 'As-of date not recorded'}
            </div>
          </div>
        );
      }
      if (row.attestationMethod) {
        return (
          <EnumText
            map={ATTESTATION_METHOD_LABEL}
            value={row.attestationMethod}
            className="text-sm font-sans text-primary leading-normal"
          />
        );
      }
      return <span className="text-sm font-sans text-tertiary leading-normal">Not advisor attested</span>;
    },
  },
];

// ---------------------------------------------------------------------------
// Consent proof card
// ---------------------------------------------------------------------------

/**
 * consentAcknowledged is optional on PledgeConsentProof. An absent value is a
 * snapshot that never recorded the acknowledgment, which is not the same fact
 * as a recorded false, so it prints "Not recorded" rather than "No".
 */
function consentAcknowledgedText(acknowledged: boolean | null | undefined): string {
  if (acknowledged === undefined || acknowledged === null) return 'Not recorded';
  return acknowledged ? 'Yes' : 'No';
}

function ConsentProofCard({ consent }: { consent: PledgeConsentProof }) {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-4 h-4 text-tertiary" aria-hidden="true" />
        <Heading level={3} size="card">
          Consent Proof
        </Heading>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        <div>
          <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
            Acknowledged
          </div>
          <BodyText variant="ui" className="text-primary">
            {consentAcknowledgedText(consent.consentAcknowledged)}
          </BodyText>
        </div>
        <div>
          <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
            Timestamp
          </div>
          <BodyText variant="ui" className="text-primary">
            {formatDateTime(consent.consentTimestamp)}
          </BodyText>
        </div>
        <div>
          <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
            IP Address
          </div>
          <BodyText variant="ui" className="text-primary font-mono text-sm">
            {consent.consentIpAddress ?? '--'}
          </BodyText>
        </div>
        <div>
          <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
            Method
          </div>
          <BodyText variant="ui" className="text-primary">
            <EnumText map={CONSENT_METHOD_LABEL} value={consent.consentMethod} />
          </BodyText>
        </div>
        <div>
          <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
            Terms Version
          </div>
          <BodyText variant="ui" className="text-primary">
            {consent.consentTermsVersion ?? '--'}
          </BodyText>
        </div>
        <div className="sm:col-span-2">
          <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
            User Agent
          </div>
          <BodyText variant="ui" className="text-secondary text-sm break-all">
            {consent.consentUserAgent ?? '--'}
          </BodyText>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AcknowledgePanel
// ---------------------------------------------------------------------------

function AcknowledgePanel({ pledgeId }: { pledgeId: string }) {
  const [notes, setNotes] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const acknowledge = useAcknowledgePledge();

  const handleAcknowledge = async () => {
    await acknowledge.mutateAsync({ pledgeId, notes: notes.trim() || undefined });
    setAcknowledged(true);
  };

  if (acknowledged || acknowledge.isSuccess) {
    return (
      <Card>
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-growth flex-shrink-0" />
          <div>
            <Heading level={3} size="card" className="text-growth">
              Pledge Acknowledged
            </Heading>
            <BodyText variant="ui" className="text-secondary text-sm mt-1">
              This pledge has been marked as acknowledged. The institution will be notified.
            </BodyText>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-warning" />
        <Heading level={3} size="card">
          Acknowledge Pledge
        </Heading>
      </div>
      <BodyText variant="ui" className="text-secondary mb-4">
        Acknowledging this pledge confirms the institution has reviewed the
        collateral selection and consent proof. This action is recorded in the
        audit trail.
      </BodyText>

      {acknowledge.isError && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
          <span className="text-sm font-sans text-error">
            {acknowledge.error?.message ?? 'Failed to acknowledge pledge.'}
          </span>
        </div>
      )}

      {/* The kit Textarea ties its label to the field, which the raw
          label/textarea pair did not, so the field had no accessible name. */}
      <div className="mb-4">
        <Textarea
          id="acknowledge-notes"
          label="Notes (optional)"
          value={notes}
          onChange={setNotes}
          placeholder="Internal notes for this acknowledgment..."
          rows={3}
        />
      </div>

      <Button
        variant="primary"
        icon={CheckCircle}
        onClick={handleAcknowledge}
        disabled={acknowledge.isPending}
      >
        {acknowledge.isPending ? 'Acknowledging...' : 'Acknowledge Pledge'}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PledgeDetailPage
// ---------------------------------------------------------------------------

/**
 * Pledge detail page -- full view of a single pledge proposal.
 *
 * Fetches from GET /api/admin/pledges/:pledgeId.
 * Allows platform admins to acknowledge PENDING pledges.
 */
export function PledgeDetailPage() {
  const { pledgeId } = useParams<{ pledgeId: string }>();
  const { data: response, isLoading, error } = usePledgeDetail(pledgeId);

  const pledge = response?.pledge;

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div>
        <RouterBackLink to="/pledges">Back to Pledges</RouterBackLink>
        <div className="space-y-6">
          <Card>
            <Skeleton className="h-8 w-48 mb-4" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i}>
                  <Skeleton className="h-3 w-20 mb-2" />
                  <Skeleton className="h-6 w-28" />
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <Skeleton className="h-6 w-32 mb-4" />
            <Skeleton className="h-48 w-full" />
          </Card>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------

  if (error || !pledge) {
    return (
      <div>
        <RouterBackLink to="/pledges">Back to Pledges</RouterBackLink>
        <Card>
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
            <Heading level={3} size="card" className="text-error">
              Unable to load pledge
            </Heading>
          </div>
          <BodyText variant="ui" className="text-secondary">
            {error?.message ?? 'Pledge not found or could not be retrieved.'}
          </BodyText>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* Back navigation */}
      <RouterBackLink to="/pledges">Back to Pledges</RouterBackLink>

      {/* Header: the pledge's subject names the page, on the canvas, the way
          Tenant Detail names its tenant. The card holds the field grid. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <Heading level={2}>{pledge.memberName ?? pledge.memberEmail}</Heading>
          {pledge.memberName && (
            <BodyText variant="ui" className="text-tertiary mt-1">
              {pledge.memberEmail}
            </BodyText>
          )}
          {pledge.fiName && (
            <BodyText variant="ui" className="text-tertiary text-sm mt-0.5">
              {pledge.fiName}
            </BodyText>
          )}
          <BodyText variant="ui" className="text-tertiary text-sm font-mono mt-0.5">
            {pledge.collateralPackageId} | {pledge.id}
          </BodyText>
        </div>
        <div className="flex flex-wrap gap-2">
          <PledgeStatusBadge status={pledge.status} />
        </div>
      </div>

      <Card className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
              Total Collateral
            </div>
            <BodyText variant="ui" className="text-primary font-medium font-mono tabular-nums">
              {formatMoney(pledge.totalCollateralValue)}
            </BodyText>
          </div>

          <div>
            <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
              Total Pledgeable
            </div>
            <BodyText variant="ui" className="text-primary font-medium font-mono tabular-nums">
              {formatMoney(pledge.totalPledgeableAmount)}
            </BodyText>
          </div>

          {pledge.currentChaTier && (
            <div>
              <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
                CHA Tier
              </div>
              <ChaTierBadge tier={pledge.currentChaTier} />
            </div>
          )}

          <div>
            <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
              Created
            </div>
            <BodyText variant="ui" className="text-primary">
              {formatDateTime(pledge.createdAt)}
            </BodyText>
          </div>

          {pledge.expiresAt && (
            <div>
              <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
                Expires
              </div>
              <BodyText variant="ui" className="text-primary">
                {formatDateTime(pledge.expiresAt)}
              </BodyText>
            </div>
          )}

          {pledge.acknowledgedBy && (
            <div>
              <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
                Acknowledged By
              </div>
              <BodyText variant="ui" className="text-primary">
                {pledge.acknowledgedBy}
              </BodyText>
            </div>
          )}
        </div>

        {pledge.notes && (
          <div className="mt-4 pt-4 border-t border-recessed">
            <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-1">
              Notes
            </div>
            <BodyText variant="ui" className="text-secondary">
              {pledge.notes}
            </BodyText>
          </div>
        )}
      </Card>

      {/* Accounts table */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Heading level={3} size="card">
            Selected Accounts ({pledge.accounts.length})
          </Heading>
        </div>
        <Table<PledgeSelectedAccountDetail>
          data={pledge.accounts}
          columns={ACCOUNT_COLUMNS}
          hoverable
        />

        {/* WHAT is pledged in each account that pledges named holdings: the
            securities and units, not only the dollar value. */}
        {pledge.accounts
          .filter((account) => (account.holdings?.length ?? 0) > 0)
          .map((account) => (
            <div key={account.id} className="mt-6 pt-4 border-t border-recessed">
              <Heading level={4} size="label" className="mb-3">
                Pledged holdings in {account.accountName} ({formatCount(account.holdings?.length)})
              </Heading>
              <Table<PledgeSelectedHoldingDetail>
                data={account.holdings ?? []}
                columns={HOLDING_COLUMNS}
                hoverable
              />
            </div>
          ))}
      </Card>

      {/* Consent proof */}
      {pledge.consent && (
        <div className="mb-6">
          <ConsentProofCard consent={pledge.consent} />
        </div>
      )}

      {/* Acknowledge panel -- only shown for PENDING pledges */}
      {pledge.status === 'PENDING' && pledgeId && (
        <AcknowledgePanel pledgeId={pledgeId} />
      )}
    </div>
  );
}
